import React from 'react';
import { get, flow } from 'lodash/fp';
import { formatDateForCoordinator } from 'lib/dateTimeFormat';
import { Query } from 'react-apollo';
import EventTracking from 'lib/EventTracking';

import Row from 'components/Row';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import { ComposableTable, getTextCellGenerator } from 'components/ComposableTable';
import CoachingErrorReporter from 'coaching/CoachingErrorReporter';
import { useCoachingViewState } from 'coaching/components/CoachingView';
import CoachingCoordinatorParticipantSidebarView, {
  Participant,
} from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorParticipantSidebarView';

import GET_UNFULFILLED_PARTICIPANT_COACH_PAIRS_QUERY from 'coaching/queries/GetUnfulfilledParticipantCoachPairs.gql';
import {
  GetUnfulfilledParticipantCoachPairs,
  GetUnfulfilledParticipantCoachPairs_coaching_unfulfilledParticipantCoachPairs,
} from 'coaching/queries/__generated__/GetUnfulfilledParticipantCoachPairs';
import CoachingConstants from 'coaching/CoachingConstants';

export type UnfulfilledParticipantCoachPair = GetUnfulfilledParticipantCoachPairs_coaching_unfulfilledParticipantCoachPairs;
type UnfulfilledParticipantCoachPairWithKey = UnfulfilledParticipantCoachPair & { key: string };

const columnHeaders = ['Session date', 'Coach', 'Student', 'Parent'].map(name => ({ name }));

const getAndFormatDate = flow([
  get('earliestUnfulfilledSessionDate'),
  date => formatDateForCoordinator(new Date(date), 'ddd, MMM D'),
]);

const columnGenerators = [
  getTextCellGenerator(getAndFormatDate),
  getTextCellGenerator(get('coach.name')),
  getTextCellGenerator(get('participant.studentName')),
  getTextCellGenerator(get('participant.parentName')),
];

// ComposableTable expects a field on each element that holds the element's key.
// We need both the coach and participant to uniquely identify an UnfulfilledParticipantCoachPair,
// so we concatenate both uuids into the key for each element.
const addPairKey = (pair: UnfulfilledParticipantCoachPair) => ({
  ...pair,
  key: `${pair.participant.uuid}.${pair.coach.uuid}`,
});

export default function CoachingCoordinatorUnfulfilledView() {
  const [selectedParticipant, setSelectedParticipant] = React.useState<Participant>();
  const { closeSidebar, setSidebar } = useCoachingViewState();

  const eventPrefix = `${CoachingConstants.COORDINATOR_TOOL_EVENT_PREFIX}.sessions.unfulfilled`;
  function trackEvent(event: string) {
    EventTracking.trackKey(`${eventPrefix}.${event}`);
  }

  React.useEffect(() => {
    trackEvent('view');
    return function cleanup() {
      closeSidebar();
    };
  }, []);

  function onSelectRow(row: UnfulfilledParticipantCoachPair) {
    trackEvent('select_participant.click');
    setSelectedParticipant(row.participant);
    setSidebar(<CoachingCoordinatorParticipantSidebarView participant={row.participant} />);
  }

  // There could be multiple rows with the same participant, but different coaches.
  // Identify them by finding all the pair keys beginning with the participant's uuid.
  const keysWithSelectedParticipant = (pairsWithKeys: UnfulfilledParticipantCoachPairWithKey[]) =>
    selectedParticipant &&
    pairsWithKeys
      .filter(pair => pair.key.startsWith(selectedParticipant.uuid))
      .map(pair => pair.key);

  return (
    <Query<GetUnfulfilledParticipantCoachPairs>
      query={GET_UNFULFILLED_PARTICIPANT_COACH_PAIRS_QUERY}
      fetchPolicy="network-only">
      {({ loading, error, data }) => {
        if (loading) {
          return <CenteredLoadingIndicator />;
        } else if (!data?.coaching_unfulfilledParticipantCoachPairs || error) {
          return (
            <CoachingErrorReporter
              error={error ?? 'Unable to load and render unfulfilled session data'}
            />
          );
        }
        const pairsWithKeys = data.coaching_unfulfilledParticipantCoachPairs.map(addPairKey);
        return (
          <Row>
            <ComposableTable<UnfulfilledParticipantCoachPairWithKey>
              rows={pairsWithKeys}
              columnHeaders={columnHeaders}
              columnGenerators={columnGenerators}
              fixedHeader
              keyField="key"
              handleRowClick={onSelectRow}
              keysOfActiveRows={keysWithSelectedParticipant(pairsWithKeys)}
            />
          </Row>
        );
      }}
    </Query>
  );
}
